import React, { useCallback } from "react";
import { CounterpartyCreationResponse } from "@common/contracts/counterparty.contract";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { useCreateCounterpartyMutation } from "@api/counterparty.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    CounterpartyForm,
    CounterpartyFormFields,
    CounterpartyFormSubmission,
    CounterpartyFormSubmitHelpers,
    FormSchema,
} from "./CounterpartyForm";
import { CounterpartyVerifiedToast } from "./CounterpartyVerifiedToast/CounterpartyVerifiedToast";

interface Props {
    onCreated: (response: CounterpartyCreationResponse) => void | Promise<void>;
    onCancelled: () => void;
    initialValues?: Partial<FormSchema>;
    fullWidth?: boolean;
    forTransactionEditing?: boolean;
    sectionClassName?: string;
    formId?: string;
    footer?: "form" | "none";
    dirtyRef?: React.MutableRefObject<boolean>;
    valuesRef?: React.MutableRefObject<FormSchema | null>;
    submitRef?: React.MutableRefObject<(() => Promise<void>) | null>;
    onSubmittingChange?: (isSubmitting: boolean) => void;
    fields?: CounterpartyFormFields;
    betweenBasicInfoAndTax?: React.ReactNode;
}

export const CreateCounterparty: React.FC<Props> = ({
    onCreated,
    onCancelled,
    initialValues,
    fullWidth,
    forTransactionEditing,
    sectionClassName,
    formId,
    footer,
    dirtyRef,
    valuesRef,
    submitRef,
    onSubmittingChange,
    fields,
    betweenBasicInfoAndTax,
}) => {
    const { toast } = useToaster();

    useKeyboardCommand({
        keys: "escape",
        callback: () => onCancelled(),
        options: {
            description: "Close counterparty form",
        },
    });

    const createCounterparty = useCreateCounterpartyMutation();
    const onSubmit = useCallback(
        async (
            data: CounterpartyFormSubmission,
            { saveTinOrNotify }: CounterpartyFormSubmitHelpers,
        ) => {
            const response = await createCounterparty.mutateAsync(data);
            await saveTinOrNotify(response.counterparty, "Created");
            await onCreated(response);

            if (
                !forTransactionEditing &&
                response.revealedOnTransactionsNumber
            ) {
                toast(
                    <CounterpartyVerifiedToast
                        counterparty={response.counterparty}
                        transactionsNumber={
                            response.revealedOnTransactionsNumber
                        }
                    />,
                    `counterparty-verified-${response.counterparty.id}`,
                );
            }
        },
        [createCounterparty, onCreated, forTransactionEditing, toast],
    );

    return (
        <CounterpartyForm
            onSubmit={onSubmit}
            onCancelled={onCancelled}
            initialValues={initialValues}
            fullWidth={fullWidth}
            submitText="Add"
            sectionClassName={sectionClassName}
            formId={formId}
            footer={footer}
            dirtyRef={dirtyRef}
            valuesRef={valuesRef}
            submitRef={submitRef}
            onSubmittingChange={onSubmittingChange}
            fields={fields}
            betweenBasicInfoAndTax={betweenBasicInfoAndTax}
        />
    );
};
